import { ChevronDown, ChevronUp, PortfolioBalanceIconInheritFill } from 'assets/icons';
import Dropdown, { DropdownItem } from 'components/Dropdown';
import { AppContext } from 'context/ContextProvider';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext, useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SettingsEventTypes } from 'types/AppType';
import { NavigationPath } from 'types/DomainTypes';
import { usePortfolioBalances } from 'views/Portfolio/components/Summary/hooks/usePortfolioBalances';
import { usePortfolioHoldingsTotals } from 'hooks/usePortfolioHoldingsTotals';
import { useFetchCashBalance } from 'views/shared/hooks/useFetchCashBalance';

interface IPortfolioCard {
  t: TFunction<'translation', undefined>;
  portfolioName?: string;
  currentHoldings?: string;
  balance?: string;
  pendingBalance?: string;
  showBorder?: boolean;
  classname?: string;
  isTotalText?: boolean;
  isDropdownItem?: boolean;
  disableOnClick?: boolean;
}

const PortfolioCard = ({
  t,
  balance,
  pendingBalance,
  currentHoldings,
  portfolioName,
  showBorder,
  classname,
  isTotalText,
  isDropdownItem,
  disableOnClick,
}: IPortfolioCard) => {
  const styles = `font-medium items-center gap-1 ${isDropdownItem ? 'flex' : 'hidden sm:flex'}`;
  return (
    <div
      onClick={(e) => {
        if (disableOnClick) e.stopPropagation();
      }}
      className={`flex flex-col text-gray-800 dark:text-gray-200 text-left text-sm ${
        showBorder ? 'border-b border-b-[#ff906d] dark:border-b-teal-400' : ''
      } ${classname}`}
    >
      <p className="font-medium whitespace-break-spaces text-[14px]">{portfolioName}</p>

      <div className={styles}>
        <p className="text-gray-600 dark:text-gray-400">
          {isTotalText ? t('common:totalPortfolioValue') : t('common:portfolioValue')}:{' '}
        </p>
        <p className="font-normal">{currentHoldings}</p>
      </div>

      <div className={styles}>
        <p className="text-gray-600 dark:text-gray-400">
          {isTotalText ? t('common:totalAvailableBalance') : t('common:availableBalance')}:{' '}
        </p>
        <p className="font-normal"> {balance}</p>
      </div>

      <div className={styles}>
        <p className="text-gray-600 dark:text-gray-400">
          {isTotalText ? t('common:totalPendingBalance') : t('common:pendingBalance')}:{' '}
        </p>
        <p className="font-normal"> {pendingBalance}</p>
      </div>
    </div>
  );
};

const PortfolioSelector = () => {
  const {
    state: {
      settings: { selectedPortfolioId },
    },
    dispatch,
    formatter: currencyFormatter,
    gbpToTargetCurrency,
  } = useContext(AppContext);

  const { managementFeeUpdates14758 } = useFlags();
  const { t } = useTranslation();
  const location = useLocation();

  const { data: cashBalanceResponse, isLoading: loadingCashBalances } = useFetchCashBalance();
  const { portfolioBalances, loading: loadingBalances } = usePortfolioBalances();
  const { totalsByPortfolioId, grandTotal, isLoading: loadingHoldings } = usePortfolioHoldingsTotals();

  const disableOnClick = ![NavigationPath.MY_PORTFOLIO, NavigationPath.MY_CELLAR].includes(
    location.pathname as NavigationPath,
  );

  const [arrowDirections, setArrowDirections] = useState(false);

  const portfoliosItems = useMemo(() => {
    if (loadingBalances || loadingCashBalances) return { items: [], dropdown: [] };
    const items = (portfolioBalances ?? []).map((x, index) => {
      const balance = String(
        currencyFormatter.format(
          cashBalanceResponse?.balances?.find((q) => String(q?.portfolioId ?? '') === String(x?.portfolioId ?? ''))
            ?.balance ?? 0,
          true,
        ),
      );
      const pendingBalance = String(currencyFormatter.format(x.balancePending ?? 0, true));
      const portfolioName = String(x.portfolioName);
      const portfolioId = String(x?.portfolioId ?? '');
      const showBorder = index !== portfolioBalances.length - 1;
      const isTotalText = index === 0;

      // Use holdings totals from PortalCurrentHoldings (single call, grouped by portfolioId); fall back to valuation while loading
      const holdingsValue = loadingHoldings
        ? x.valuation ?? 0
        : index === 0
        ? grandTotal
        : totalsByPortfolioId[portfolioId] ?? x.valuation ?? 0;
      const currentHoldings = String(gbpToTargetCurrency.convertAndFormat(holdingsValue));

      return {
        portfolioId,
        balance,
        pendingBalance,
        portfolioName,
        currentHoldings,
        showBorder,
        isTotalText,
      };
    });

    const dropdown = (items ?? []).map((x) => {
      const { balance, pendingBalance, currentHoldings, portfolioId, portfolioName, isTotalText, showBorder } = x;

      return {
        id: portfolioId,
        value: balance,
        text: portfolioName,
        content: (
          <PortfolioCard
            disableOnClick={disableOnClick}
            t={t}
            portfolioName={portfolioName}
            balance={balance}
            pendingBalance={pendingBalance}
            showBorder={showBorder}
            currentHoldings={currentHoldings}
            isTotalText={isTotalText}
            classname="py-3"
            isDropdownItem={true}
          />
        ),
      };
    });

    return { items, dropdown };
  }, [
    cashBalanceResponse,
    loadingBalances,
    t,
    portfolioBalances,
    currencyFormatter,
    gbpToTargetCurrency,
    loadingCashBalances,
    disableOnClick,
    totalsByPortfolioId,
    grandTotal,
    loadingHoldings,
  ]);

  const seletedPortfolio = useMemo(() => {
    const portfolioInfo =
      portfoliosItems.items.find((x) => x.portfolioId === String(selectedPortfolioId)) ?? portfoliosItems.items?.[0];

    return portfolioInfo;
  }, [selectedPortfolioId, portfoliosItems]);

  const onItemSelect = (item: DropdownItem) => {
    setArrowDirections(false);
    dispatch({
      type: SettingsEventTypes.UPDATE_SETTINGS,
      payload: {
        selectedPortfolioId: Number(item.id),
      },
    });
  };

  useEffect(() => {
    if (location.pathname === NavigationPath.PORTFOLIO) {
      dispatch({
        type: SettingsEventTypes.UPDATE_SETTINGS,
        payload: {
          selectedPortfolioId: Number(''),
        },
      });
    }
  }, [location.pathname, dispatch]);

  return (
    <div className="w-full sm:w-[380px]">
      {managementFeeUpdates14758 && (
        <Dropdown
          usePortal={true}
          disabledActiveState={disableOnClick}
          containerClassName="w-full sm:w-[380px]"
          onOpen={() => setArrowDirections(true)}
          onClose={() => setArrowDirections(false)}
          itemsWrapperClassName="w-full sm:w-[380px] overflow-hidden"
          itemClassName={`!py-0 text-base w-full ${
            disableOnClick ? '!cursor-default !bg-white dark:!bg-slate-800' : ''
          }`}
          className="text-sm w-full h-16 p-0 justify-start sm:h-24 sm:w-[380px]"
          itemsContainerClassName="w-full !py-0"
          items={portfoliosItems.dropdown}
          onItemSelect={onItemSelect}
        >
          <div className="flex items-center border border-gray-300 dark:border-slate-600 rounded-xl p-3 text-gray-800 dark:text-gray-200 bg-white dark:bg-slate-800 transition-colors">
            <div className="flex flex-row items-center">
              <div className="w-[16px] h-[13px] mr-2 text-gray-700 dark:text-gray-300">
                <PortfolioBalanceIconInheritFill className="w-full h-full" />
              </div>

              <PortfolioCard
                t={t}
                portfolioName={seletedPortfolio?.portfolioName}
                balance={seletedPortfolio?.balance}
                pendingBalance={seletedPortfolio?.pendingBalance}
                currentHoldings={seletedPortfolio?.currentHoldings}
                isTotalText={seletedPortfolio?.isTotalText}
              />
            </div>

            <div className="ml-auto text-gray-500 dark:text-gray-400">
              {!arrowDirections && <ChevronDown aria-hidden="true" />}
              {arrowDirections && <ChevronUp aria-hidden="true" />}
            </div>
          </div>
        </Dropdown>
      )}
    </div>
  );
};

export default PortfolioSelector;
